import { css } from '@styled-system/css';
import { center } from '@styled-system/patterns';
import { token } from '@styled-system/tokens';
import { Table } from 'antd';
import type { FC } from 'react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { PuffLoader } from 'react-spinners';

import { Button } from '@/shared/components/ui/Button';
import { FilterTag } from '@/shared/components/ui/FilterTag';
import { Message } from '@/shared/components/ui/Message';
import { SearchBarNew } from '@/shared/components/ui/SearchBarNew';
import { useIsMobile } from '@/shared/hooks/useIsMobile';

import { useAnalysisNiches } from '../hooks/useAnalysisNiches';
import { getFormattedNicheName } from '../utils/formatNicheName';
import type { ColumnsType } from 'antd/es/table';

import type { AnalysisNiche, FilterValues } from '../types';
import { NicheFilterModal } from './NicheFilterModal';
import { NicheSellsHistModal } from './NicheSellsHistModal';

const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  return value < 1 && value !== 0 ? value.toFixed(2) : value.toFixed(0);
};

const formatPrice = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  return `${value.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;
};

const formatPercentage = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return '-';
  return `${formatNumber(value)}%`;
};

const formatTimeToSell = (hours: number | null | undefined): string => {
  if (hours === null || hours === undefined) return '-';
  const days = Math.floor(hours / 24);
  const remainingHours = Math.floor(hours % 24);
  if (days === 0) return `${remainingHours}h`;
  return `${days}j ${remainingHours}h`;
};

const formatNicheName = (niche: string, allNiches: AnalysisNiche[]) => {
  const formatted = getFormattedNicheName(niche, allNiches);

  // If no path (simple case or no duplicate)
  if (!formatted.path || !formatted.hasDuplicate) {
    return (
      <span className={css({ fontSize: '12px', color: 'text.primary', fontFamily: 'Manrope, sans-serif' })}>
        {formatted.name}
      </span>
    );
  }

  const maxPathLength = 40;
  const truncatedPath =
    formatted.path.length > maxPathLength ? `...${formatted.path.slice(-maxPathLength)}` : formatted.path;

  // If duplicate, show truncated path + last category on 2 lines
  return (
    <div className={css({ display: 'flex', flexDirection: 'column', gap: '1' })}>
      <span className={css({ fontSize: '11px', color: 'text.muted', fontFamily: 'Manrope, sans-serif' })}>
        {truncatedPath}
      </span>
      <span
        className={css({
          fontSize: '12px',
          color: 'text.primary',
          fontFamily: 'Manrope, sans-serif',
          alignSelf: 'flex-start',
        })}
      >
        &gt; {formatted.name}
      </span>
    </div>
  );
};

export const AnalysisPageNiches: FC = () => {
  const isMobile = useIsMobile();
  const { id: analysisId } = useParams<{ id: string }>();
  const { niches, isLoading, error } = useAnalysisNiches(analysisId ?? '');
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState<FilterValues>({});
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterButtonRef, setFilterButtonRef] = useState<HTMLDivElement | null>(null);
  const [selectedNiche, setSelectedNiche] = useState<AnalysisNiche | null>(null);

  const removeFilter = (...keys: (keyof FilterValues)[]) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      keys.forEach((key) => delete newFilters[key]);
      return newFilters;
    });
  };

  const handleExport = () => {
    // Prepare CSV headers
    const headers = [
      'Type',
      'Niche',
      'Produits analysés',
      'Prix moyen',
      '% de vente',
      'Ventes par jour',
      'Temps de vente (heures)',
    ];

    // Prepare CSV rows
    const rows = filteredNiches.map((niche) => [
      niche.type === 'brand' ? 'Marque' : 'Catégorie',
      niche.niche,
      niche.productCount.toString(),
      niche.avgPrice ? niche.avgPrice.toFixed(2) : '',
      niche.soldPct ? niche.soldPct.toFixed(2) : '',
      niche.soldPerDay ? niche.soldPerDay.toFixed(2) : '',
      niche.timeToSellHours ? niche.timeToSellHours.toFixed(2) : '',
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        row
          .map((cell) => (cell.includes(',') || cell.includes('"') ? `"${cell.replace(/"/g, '""')}"` : cell))
          .join(','),
      ),
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `niches_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredNiches = niches.filter((niche) => {
    if (searchText && !niche.niche.toLowerCase().includes(searchText.toLowerCase())) {
      return false;
    }
    if (filters.avgPriceMin !== undefined && niche.avgPrice && niche.avgPrice < filters.avgPriceMin) {
      return false;
    }
    if (filters.avgPriceMax !== undefined && niche.avgPrice && niche.avgPrice > filters.avgPriceMax) {
      return false;
    }
    if (filters.productCountMin !== undefined && niche.productCount < filters.productCountMin) {
      return false;
    }
    if (filters.productCountMax !== undefined && niche.productCount > filters.productCountMax) {
      return false;
    }
    if (filters.soldPctMin !== undefined && niche.soldPct && niche.soldPct < filters.soldPctMin) {
      return false;
    }
    if (filters.soldPctMax !== undefined && niche.soldPct && niche.soldPct > filters.soldPctMax) {
      return false;
    }
    if (filters.soldPerDayMin !== undefined && niche.soldPerDay && niche.soldPerDay < filters.soldPerDayMin) {
      return false;
    }
    if (filters.soldPerDayMax !== undefined && niche.soldPerDay && niche.soldPerDay > filters.soldPerDayMax) {
      return false;
    }
    if (filters.timeToSellMin !== undefined && niche.timeToSellHours && niche.timeToSellHours < filters.timeToSellMin) {
      return false;
    }
    if (filters.timeToSellMax !== undefined && niche.timeToSellHours && niche.timeToSellHours > filters.timeToSellMax) {
      return false;
    }
    if (filters.nicheTypes && filters.nicheTypes.length > 0 && !filters.nicheTypes.includes(niche.type)) {
      return false;
    }
    return true;
  });

  // Define table columns
  const columns: ColumnsType<AnalysisNiche> = [
    {
      title: 'Niche',
      dataIndex: 'niche',
      key: 'niche',
      width: 190,
      render: (niche: string, record: AnalysisNiche) => (
        <div className={css({ display: 'flex', gap: '2', alignItems: 'center' })}>
          {record.imageUrl ? (
            <img
              src={record.imageUrl}
              alt={niche}
              className={css({
                w: '28px',
                h: '28px',
                minW: '28px',
                minH: '28px',
                borderRadius: '999px',
                objectFit: 'cover',
              })}
            />
          ) : (
            <div
              className={css({
                w: '28px',
                h: '28px',
                minW: '28px',
                minH: '28px',
                borderRadius: '999px',
                bg: 'bg.selected',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: '500',
                color: 'text.secondary',
              })}
            >
              {niche.charAt(0).toUpperCase()}
            </div>
          )}
          {formatNicheName(niche, niches)}
        </div>
      ),
    },
    {
      title: 'Prix moyen',
      dataIndex: 'avgPrice',
      key: 'avgPrice',
      render: formatPrice,
      sorter: (a, b) => (a.avgPrice ?? 0) - (b.avgPrice ?? 0),
    },
    {
      title: 'Produits analysés',
      dataIndex: 'productCount',
      key: 'productCount',
      render: formatNumber,
      sorter: (a, b) => a.productCount - b.productCount,
    },
    {
      title: '% de vente',
      dataIndex: 'soldPct',
      key: 'soldPct',
      render: formatPercentage,
      sorter: (a, b) => (a.soldPct ?? 0) - (b.soldPct ?? 0),
    },
    {
      title: 'Ventes par jour',
      dataIndex: 'soldPerDay',
      key: 'soldPerDay',
      render: formatNumber,
      sorter: (a, b) => (a.soldPerDay ?? 0) - (b.soldPerDay ?? 0),
    },
    {
      title: 'Ajouts par jour',
      dataIndex: 'addedPerDay',
      key: 'addedPerDay',
      render: formatNumber,
      sorter: (a, b) => (a.addedPerDay ?? 0) - (b.addedPerDay ?? 0),
    },
    {
      title: 'Temps de vente',
      dataIndex: 'timeToSellHours',
      key: 'timeToSellHours',
      render: formatTimeToSell,
      sorter: (a, b) => (a.timeToSellHours ?? 0) - (b.timeToSellHours ?? 0),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: unknown, record: AnalysisNiche) => (
        <a
          onClick={() => setSelectedNiche(record)}
          className={css({
            color: '#258bff',
            cursor: 'pointer',
            fontSize: '12px',
            fontFamily: 'Manrope, sans-serif',
            _hover: {
              textDecoration: 'underline',
            },
          })}
        >
          Voir les ventes
        </a>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className={center({ w: 'full', h: 'full' })}>
        <PuffLoader color={token.var('colors.accent.primary')} size={60} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={center({ w: 'full', h: 'full' })}>
        <Message type="error" bordered>
          {error}
        </Message>
      </div>
    );
  }

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: 'md',
        w: 'full',
        h: 'full',
        px: '2',
      })}
    >
      {/* Header Section */}
      <div className={css({ display: 'flex', flexDirection: 'column', gap: '4' })}>
        {/* Title Row */}
        <div
          className={css({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          })}
        >
          <h2
            className={css({
              fontSize: '14px',
              fontWeight: '500',
              color: 'text.primary',
              fontFamily: 'Manrope, sans-serif',
              m: 0,
            })}
          >
            Repartition des niches
          </h2>
          <Button handleClick={handleExport} icon="download">
            Exporter
          </Button>
        </div>

        {/* Search and Filters Row */}
        <div
          className={css({
            display: 'flex',
            gap: '3',
            alignItems: 'center',
            flexWrap: 'wrap',
          })}
        >
          {/* Search Bar */}
          <SearchBarNew placeholder="Search" value={searchText} onChange={setSearchText} />

          {/* Filters Button */}
          {!isMobile && (
            <div ref={setFilterButtonRef} className={css({ position: 'relative' })}>
              <Button
                handleClick={() => setShowFilterModal(!showFilterModal)}
                icon="add"
                className={css({ height: '2rem' })}
              >
                Filtres
              </Button>
              {/* Filter Modal */}
              <NicheFilterModal
                isOpen={showFilterModal}
                filters={filters}
                onClose={() => setShowFilterModal(false)}
                onFilterChange={setFilters}
                buttonRef={filterButtonRef}
              />
            </div>
          )}

          {/* Filter Tags */}
          {(filters.avgPriceMin !== undefined || filters.avgPriceMax !== undefined) && (
            <FilterTag
              name="Prix moyen"
              value={
                filters.avgPriceMin !== undefined && filters.avgPriceMax !== undefined
                  ? `${filters.avgPriceMin} € - ${filters.avgPriceMax} €`
                  : filters.avgPriceMin !== undefined
                    ? `${filters.avgPriceMin} € min.`
                    : `${filters.avgPriceMax} € max.`
              }
              onRemove={() => removeFilter('avgPriceMin', 'avgPriceMax')}
            />
          )}

          {(filters.productCountMin !== undefined || filters.productCountMax !== undefined) && (
            <FilterTag
              name="Produits analysés"
              value={
                filters.productCountMin !== undefined && filters.productCountMax !== undefined
                  ? `${filters.productCountMin} - ${filters.productCountMax}`
                  : filters.productCountMin !== undefined
                    ? `${filters.productCountMin} min.`
                    : `${filters.productCountMax} max.`
              }
              onRemove={() => removeFilter('productCountMin', 'productCountMax')}
            />
          )}

          {(filters.soldPctMin !== undefined || filters.soldPctMax !== undefined) && (
            <FilterTag
              name="% de vente"
              value={
                filters.soldPctMin !== undefined && filters.soldPctMax !== undefined
                  ? `${filters.soldPctMin} % - ${filters.soldPctMax} %`
                  : filters.soldPctMin !== undefined
                    ? `${filters.soldPctMin} % min.`
                    : `${filters.soldPctMax} % max.`
              }
              onRemove={() => removeFilter('soldPctMin', 'soldPctMax')}
            />
          )}

          {(filters.soldPerDayMin !== undefined || filters.soldPerDayMax !== undefined) && (
            <FilterTag
              name="Ventes par jour"
              value={
                filters.soldPerDayMin !== undefined && filters.soldPerDayMax !== undefined
                  ? `${formatNumber(filters.soldPerDayMin)} - ${formatNumber(filters.soldPerDayMax)}`
                  : filters.soldPerDayMin !== undefined
                    ? `${formatNumber(filters.soldPerDayMin)} min.`
                    : `${formatNumber(filters.soldPerDayMax!)} max.`
              }
              onRemove={() => removeFilter('soldPerDayMin', 'soldPerDayMax')}
            />
          )}

          {(filters.timeToSellMin !== undefined || filters.timeToSellMax !== undefined) && (
            <FilterTag
              name="Temps de vente"
              value={
                filters.timeToSellMin !== undefined && filters.timeToSellMax !== undefined
                  ? `${filters.timeToSellMin}h - ${filters.timeToSellMax}h`
                  : filters.timeToSellMin !== undefined
                    ? `${filters.timeToSellMin}h min.`
                    : `${filters.timeToSellMax}h max.`
              }
              onRemove={() => removeFilter('timeToSellMin', 'timeToSellMax')}
            />
          )}

          {filters.nicheTypes !== undefined && filters.nicheTypes.length > 0 && (
            <FilterTag
              name="Type de niche"
              value={filters.nicheTypes.map((t) => (t === 'brand' ? 'Marque' : 'Catégorie')).join(', ')}
              onRemove={() => removeFilter('nicheTypes')}
            />
          )}
        </div>
      </div>

      {/* Ant Design Table */}
      <div className={css({ flex: 1, overflow: 'auto' })}>
        <Table<AnalysisNiche>
          columns={columns}
          dataSource={filteredNiches}
          rowKey={(record) => `${record.type}-${record.niche}`}
          pagination={{
            pageSize: 100,
          }}
        />
      </div>

      {/* Modal de ventes */}
      {selectedNiche && (
        <NicheSellsHistModal
          analysisId={analysisId ?? ''}
          brandOrFullPath={selectedNiche.niche}
          nicheImageUrl={selectedNiche.imageUrl ?? undefined}
          niche={selectedNiche.niche}
          allNiches={niches}
          onClose={() => setSelectedNiche(null)}
        />
      )}
    </div>
  );
};
